import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService } from '@angular-workspace/core';

/**
 * Injection token for sandbox API base URL
 */
export const SANDBOX_BASE_URL = new InjectionToken<string>('SANDBOX_BASE_URL');

/**
 * Sandbox API Service for handling sandbox-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class SandboxApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(SANDBOX_BASE_URL) private sandboxBaseUrl: string,
  ) {}

  /**
   * List all notebooks
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with list of notebooks
   */
  async listNotebooks(baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/list`;
      console.log('SandboxApiService: Calling listNotebooks API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listNotebooks API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing notebooks:', error);
      throw error;
    }
  }

  /**
   * List notebooks from an absolute URL.
   * Used when a specific environment requires a one-off endpoint.
   * @param url Fully qualified notebooks endpoint URL
   * @returns Promise with list of notebooks
   */
  async listNotebooksFromUrl(url: string): Promise<any> {
    try {
      console.log('SandboxApiService: Calling listNotebooksFromUrl API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listNotebooksFromUrl API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing notebooks from absolute URL:', error);
      throw error;
    }
  }

  /**
   * List available GPU instance types
   * Calls: <sandboxGpu Url>/v1/notebook/gpu-instance-types
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with list of GPU instance types
   */
  async listGpuInstances(baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      // Derive the origin from the configured sandbox base URL and build the final GPU URL.
      // Required final endpoint (as per backend): https://v2.dev.sandbox.iudx.io/api//v1/notebook/gpu-instance-types
      let origin: string;
      try {
        const parsed = new URL(base);
        origin = parsed.origin;
      } catch {
        // Fallback: if base is not a full URL, use it as-is
        origin = base.replace(/\/+$/, '');
      }

      const url = `${origin}/api//v1/notebook/gpu-instance-types`;
      console.log('SandboxApiService: Calling listGpuInstances API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: listGpuInstances API response received:', response);
      return response;
    } catch (error) {
      console.error('Error listing GPU instances:', error);
      throw error;
    }
  }

  /**
   * Create a new notebook (uses v1 path)
   * @param payload The request payload containing notebook data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the created notebook response
   */
  async createNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      // Use the configured base URL (e.g. .../sandbox-api/v1/) as-is.
      // This preserves the required path segment and avoids dropping it to origin+`/api/v1`.
      const normalizedBase = base.replace(/\/+$/, '');
      const url = `${normalizedBase}/notebook/create`;
      console.log('SandboxApiService: Calling createNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined,
        false,
      );

      console.log('SandboxApiService: createNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating notebook:', error);
      throw error;
    }
  }

  /**
   * Stop a notebook
   * @param payload The request payload containing notebook stop data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the stop notebook response
   */
  async stopNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/stop`;
      console.log('SandboxApiService: Calling stopNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: stopNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error stopping notebook:', error);
      throw error;
    }
  }

  /**
   * Start a notebook
   * @param payload The request payload containing notebook start data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the start notebook response
   */
  async startNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/start`;
      console.log('SandboxApiService: Calling startNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: startNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error starting notebook:', error);
      throw error;
    }
  }

  /**
   * Delete a notebook
   * @param payload The request payload containing notebook delete data
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the delete notebook response
   */
  async deleteNotebook(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/delete`;
      console.log('SandboxApiService: Calling deleteNotebook API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('SandboxApiService: deleteNotebook API response received:', response);
      return response;
    } catch (error) {
      console.error('Error deleting notebook:', error);
      throw error;
    }
  }

  /**
   * Check notebook status
   * @param statusIdentifier The status identifier sent from the component (e.g., notebook ID)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the notebook status response
   */
  async notebookStatusCheck(statusIdentifier: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/status/${statusIdentifier}`;
      console.log('SandboxApiService: Calling notebookStatusCheck API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: notebookStatusCheck API response received:', response);
      return response;
    } catch (error) {
      console.error('Error checking notebook status:', error);
      throw error;
    }
  }

  /**
   * Check if a notebook exists
   * @param identifier The identifier sent from the component (e.g., notebook ID)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the notebook existence check response
   */
  async checkNotebookExists(identifier: string, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}notebook/check-exists/${identifier}`;
      console.log('SandboxApiService: Calling checkNotebookExists API with URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      console.log('SandboxApiService: checkNotebookExists API response received:', response);
      return response;
    } catch (error) {
      console.error('Error checking notebook existence:', error);
      throw error;
    }
  }

  /**
   * Create a Kubeflow profile
   * @param payload The request payload containing profile data (sent from component)
   * @param baseUrl Optional base URL (when not provided, uses injected SANDBOX_BASE_URL)
   * @returns Promise with the created profile response
   */
  async createKubeFlow(payload: any, baseUrl?: string): Promise<any> {
    try {
      const base = baseUrl ?? this.sandboxBaseUrl;
      const url = `${base}profile/create`;
      console.log('SandboxApiService: Calling createKubeFlow API with URL:', url);
      console.log('SandboxApiService: Payload:', payload);

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined,
        false,
      );

      console.log('SandboxApiService: createKubeFlow API response received:', response);
      return response;
    } catch (error) {
      console.error('Error creating Kubeflow profile:', error);
      throw error;
    }
  }
}
